import compact from 'lodash/compact'
import map from 'lodash/map'
import React, { useMemo } from 'react'

import { BlockContainer } from '@Components/BlockContainer'
import { Elements } from '@Components/Elements/Types'
import { Tiles } from '@Components/Tiles'
import { TilesCardGeneric } from '@Components/Tiles/Card/Generic'
import { useTileSettings } from '@Components/Tiles/settings'
import styles from '@Components/Tiles/Tiles.module.scss'
import { Block, BlockAppearance, PagePresentation, RteField, TileResource } from '@Generated/graphql'
import { BlockContext } from '@Globals/CMS/Context'
import { getReferenceFieldValue } from '@Globals/CMS/FieldHelpers'
import { Image } from '@Globals/Image'

interface TileProps {
  appearance: BlockAppearance
  button?: {
    newTab?: boolean
    text?: string
    url?: string
  }
  campaignTag?: {
    appearance: BlockAppearance
    text: RteField
  }
  display: string[]
  elements: Elements[] | null
  image: Image
  subTitle?: RteField | null
  title: string
}

function isImage(item: any | Image): item is Image {
  const asImage = item as Image
  return (
    !!item
    && !!asImage.url
    && !!asImage.dimensions
    && (!!asImage.dimensions.height || !!asImage.dimensions.width)
  )
}

const getImageFromReference = (reference: any | null | undefined) => {
  if (!reference) {
    return null
  }

  if (!!reference.image && isImage(reference.image)) {
    return reference.image
  }

  return null
}

const getTileButton = (tile): TileProps['button'] => {
  if (!tile) {
    return undefined
  }

  const enableButton = tile.display.includes('button')
  if (!enableButton && !tile.button?.link?.url && !tile.button?.text) {
    return undefined
  }

  return {
    url: tile.button.link?.url,
    text: tile.button.text ?? '',
    newTab: !!tile.button.link?.newTab,
  }
}

const getTiles = ({ fields }: Block): TileProps[] => {
  const tiles: TileResource[] = getReferenceFieldValue(fields || [], 'tiles') ?? []

  return compact(map(tiles, (tile) => {
    const image = getImageFromReference(tile.references && tile.references[0])
    if (!image?.url && !tile?.appearance?.asset) {
      return null
    }

    return {
      appearance: tile.appearance || {},
      display: tile.display ?? [],
      campaignTag: tile.campaignTag
        ? {
          appearance: tile.campaignTag.appearance || {},
          text: tile.campaignTag.text!,
        }
        : undefined,
      title: tile.header ?? '',
      subTitle: tile.description,
      image,
      button: getTileButton(tile),
      elements: (tile.elements || null) as Elements[] | null,
    }
  }))
}

const TilesBlock = ({
  data,
  id,
}: {
  data: Block
  id?: string
}) => {
  const { presentation } = React.use(BlockContext)
  const { appearance, common, settings } = data
  const tileSettings = useTileSettings(settings)
  const tiles = useMemo(
    () => getTiles(data),
    [data],
  )

  if (!tiles || tiles.length === 0) {
    return null
  }

  return (
    <BlockContainer
      appearance={appearance || {}}
      common={common || {}}
      id={id}
      presentation={presentation}
    >
      <Tiles settings={tileSettings}>
        {map(tiles, (item, i) => {
          if (!item.image?.url && !item?.appearance?.asset) {
            return null
          }

          return (
            <a
              key={`${item.title}_${i}`}
              aria-label={item.title}
              className={styles.slide}
              href={item.button?.url || '#'}
              rel="noopener noreferrer"
              target={item.button?.newTab ? '_blank' : '_self'}
            >
              <TilesCardGeneric
                key={`TilesWithText_${item.title}`}
                appearance={item.appearance}
                buttonLabel={item.button?.text || ''}
                campaignTag={item.campaignTag}
                cardAspectRatio={tileSettings.cardAspectRatio}
                cardSize={tileSettings.cardSize}
                cardVariant={tileSettings.cardVariant}
                disableLinks
                elements={item.elements}
                height={tileSettings.height}
                image={item.image?.url}
                presentation={presentation || PagePresentation.Full}
                subTitle={item.subTitle}
                title={item.title}
                width={tileSettings.width}
              />
            </a>
          )
        })}
      </Tiles>
    </BlockContainer>
  )
}
export default TilesBlock
